import React, { useMemo } from "react";
import { Box, Grid, CircularProgress } from "@mui/material";
import { StatCard } from "../../../../../shared/components/charts";
import { getStatCardIcon } from "../../../../../shared/utils";
import type { MetricsFilters } from "../../../../../shared/api/types";
import { useProgrammeProfileStats } from "../hooks/useProgrammeProfile";

interface ProgrammeStatsColumnProps {
  filterParams: MetricsFilters;
  stacked?: boolean;
}

const METRIC_KEYS = ["total_users", "active_users", "participation_rate", "completion_rate"];

/**
 * Renders Programme Profile's headline engagement stat cards (total/active
 * users, participation, completion) for a single filter scope. Safe to render
 * once per comparison group.
 */
export const ProgrammeStatsColumn: React.FC<ProgrammeStatsColumnProps> = ({
  filterParams,
  stacked = false,
}) => {
  const { data: statsData, isLoading } = useProgrammeProfileStats(filterParams);

  const statCards = useMemo(() => {
    if (!statsData?.data) return [];
    return METRIC_KEYS.filter((key) => statsData.data[key]).map((key) => ({
      key,
      metric: statsData.data[key],
      icon: getStatCardIcon(key),
    }));
  }, [statsData]);

  const statColSize = stacked ? { xs: 12, sm: 6 } : { xs: 12, sm: 6, md: 3 };

  return (
    <Grid container spacing={2}>
      {isLoading
        ? [1, 2, 3, 4].map((i) => (
            <Grid size={statColSize} key={i}>
              <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                <CircularProgress size={24} />
              </Box>
            </Grid>
          ))
        : statCards.map((stat) => (
            <Grid size={statColSize} key={stat.key}>
              <StatCard metric={stat.metric} icon={stat.icon} />
            </Grid>
          ))}
    </Grid>
  );
};
